import type {
    CreateSalesTransactionDiscount,
    CreateSalesTransactionLine,
    CreateSalesTransactionPayment,
    CreateSalesTransactionRequest,
} from '@/api/handlers/resources/sales/requests';
import type { Discount } from './discountMath';
import type { CartLine } from './useSeller';

/** Outcome of turning the cart into committable sale lines. */
export type BuildLinesResult =
    | { ok: true; lines: CreateSalesTransactionLine[] }
    | { ok: false; reason: 'empty' };

/** Outcome of assembling the full `POST /sales/transactions` body. */
export type BuildSaleResult =
    | { ok: true; request: CreateSalesTransactionRequest }
    | { ok: false; reason: 'empty' };

/** A discount on the wire: as entered, `note` only when there is one. */
const toWireDiscount = (discount: Discount): CreateSalesTransactionDiscount => {
    const note = discount.note.trim();
    return note === ''
        ? { type: discount.type, value: discount.value }
        : { type: discount.type, value: discount.value, note };
};

/**
 * Builds the variant-keyed lines for a sale. Money is server-authoritative: we
 * send each line's variant, quantity and discount as entered, and let bos-api
 * resolve prices and recompute the total. Every cart line already names the variant the seller
 * chose — directly for a single-variant product, through the picker for a
 * several-variant one — so each becomes one sale line.
 *
 * Fails only when the cart is empty.
 */
export const buildSaleLines = (cart: CartLine[]): BuildLinesResult => {
    if (cart.length === 0) {
        return { ok: false, reason: 'empty' };
    }

    return {
        ok: true,
        lines: cart.map((line) =>
            line.discount === null
                ? { variantId: line.variant.variantId, quantity: line.qnt }
                : {
                      variantId: line.variant.variantId,
                      quantity: line.qnt,
                      discount: toWireDiscount(line.discount),
                  },
        ),
    };
};

/**
 * Assembles the `POST /sales/transactions` body from the cart, the sale
 * discount and a ready-built `payments` array (see `buildPayments` in
 * `paymentMeta`). The server applies the discounts, recomputes the total and
 * cross-checks it against the payment sum, so the caller is responsible for
 * tendering payments that cover the discounted total (`total` in
 * `useSeller`). A discount is sent only where one is set, so a cart without
 * any posts the same body it always has.
 */
export const buildSaleRequest = (
    cart: CartLine[],
    saleDiscount: Discount | null,
    payments: CreateSalesTransactionPayment[],
): BuildSaleResult => {
    const built = buildSaleLines(cart);
    if (!built.ok) {
        return built;
    }
    return {
        ok: true,
        request:
            saleDiscount === null
                ? { lines: built.lines, payments }
                : {
                      lines: built.lines,
                      payments,
                      discount: toWireDiscount(saleDiscount),
                  },
    };
};
